import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { MarketingActivity, ActivityStatus } from '@/types';
import { cn, formatCurrency, formatDate, priorityColors, getInitials } from '@/lib/utils';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, MoreVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';

const STATUS_OPTIONS: { value: ActivityStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To-Do' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Erledigt' },
];

type SortKey =
  | 'title'
  | 'type'
  | 'status'
  | 'priority'
  | 'start_date'
  | 'end_date'
  | 'budget_planned';

const PAGE_SIZE = 10;

interface ListViewProps {
  activities: MarketingActivity[];
  loading: boolean;
  onRefresh: () => void;
  onActivityClick: (id: string) => void;
}

export default function ListView({
  activities,
  loading,
  onRefresh,
  onActivityClick,
}: ListViewProps) {
  const confirmDialog = useConfirmDialog();
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [bulkStatus, setBulkStatus] = useState<ActivityStatus | ''>('');
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const arr = activities.filter((activity) => (
      !search || activity.title.toLowerCase().includes(search.toLowerCase())
    ));
    arr.sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortKey) {
        case 'title':
          va = a.title;
          vb = b.title;
          break;
        case 'type':
          va = a.type;
          vb = b.type;
          break;
        case 'status':
          va = a.status;
          vb = b.status;
          break;
        case 'priority':
          va = a.priority;
          vb = b.priority;
          break;
        case 'start_date':
          va = a.start_date ?? '';
          vb = b.start_date ?? '';
          break;
        case 'end_date':
          va = a.end_date ?? '';
          vb = b.end_date ?? '';
          break;
        case 'budget_planned':
          va = a.budget_planned;
          vb = b.budget_planned;
          break;
        default:
          return 0;
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [activities, sortKey, sortDir, search]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(0, totalPages - 1));
  const paginated = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, currentPage]);

  const allPageSelected = paginated.length > 0 && paginated.every((activity) => selectedIds.has(activity.id));

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const availableIds = new Set(activities.map((activity) => activity.id));
    setSelectedIds((previous) => new Set([...previous].filter((id) => availableIds.has(id))));
  }, [activities]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      for (const activity of paginated) {
        if (allPageSelected) next.delete(activity.id);
        else next.add(activity.id);
      }
      return next;
    });
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setUpdating(true);
    try {
      const ids = [...selectedIds];
      const results = await Promise.allSettled(
        ids.map((id) =>
          api.patch<ApiResponse<MarketingActivity>>(`/activities/${id}`, {
            status: bulkStatus,
          })
        )
      );
      const failedIds = ids.filter((_, index) => results[index]?.status === 'rejected');
      setSelectedIds(new Set(failedIds));
      if (failedIds.length > 0) {
        toast.error(`${failedIds.length} Aktivitäten konnten nicht aktualisiert werden. Bitte erneut versuchen.`);
      } else {
        setBulkStatus('');
      }
      onRefresh();
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (id: string, status: ActivityStatus) => {
    try {
      await api.patch<ApiResponse<MarketingActivity>>(`/activities/${id}`, {
        status,
      });
      onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Status konnte nicht geändert werden.');
    }
  };

  const handleDeactivateActivity = async (activity: MarketingActivity) => {
    const confirmed = await confirmDialog({
      title: 'Aktivität deaktivieren?',
      description: `«${activity.title}» wird aus der Übersicht entfernt — unabhängig vom Status (z. B. Backlog). Sie erscheint nicht mehr in Plan- und Aktivitätslisten.`,
      confirmText: 'Deaktivieren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setDeactivatingId(activity.id);
    try {
      const res = await api.patch<ApiResponse<{ message?: string }>>(`/activities/${activity.id}/deactivate`);
      if (!res.success) {
        throw new Error(res.error ?? 'Aktivität konnte nicht deaktiviert werden.');
      }
      toast.success('Aktivität deaktiviert.');
      onRefresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aktivität konnte nicht deaktiviert werden.');
    } finally {
      setDeactivatingId(null);
    }
  };

  const Th = ({
    label,
    sortKeyName,
    className,
  }: {
    label: string;
    sortKeyName: SortKey;
    className?: string;
  }) => (
    <th className={cn('px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted', className)}>
      <button
        type="button"
        className="flex items-center gap-1 hover:text-foreground"
        onClick={() => toggleSort(sortKeyName)}
      >
        {label}
        {sortKey === sortKeyName ? (
          sortDir === 'asc' ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )
        ) : null}
      </button>
    </th>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-10 px-4 py-3" />
                  <th className="px-4 py-3">Titel</th>
                  <th className="px-4 py-3">Typ</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Priorität</th>
                  <th className="px-4 py-3">Zugewiesen</th>
                  <th className="px-4 py-3">Team</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Ende</th>
                  <th className="px-4 py-3">Budget</th>
                  <th className="w-12 px-2 py-3" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border dark:border-border/50">
                    <td className="px-4 py-3">
                      <div className="h-4 w-4 animate-pulse rounded bg-card-hover" />
                    </td>
                    <td colSpan={10}>
                      <div className="h-6 w-48 animate-pulse rounded bg-card-hover" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <p className="text-muted">
            Keine Aktivitäten gefunden.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="max-w-md">
        <Input
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          placeholder="Aktivitäten in dieser Ansicht suchen..."
        />
      </div>
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-3 dark:bg-card/30">
          <span className="text-sm font-medium">
            {selectedIds.size} ausgewählt
          </span>
          <Select
            value={bulkStatus}
            onValueChange={(v) => setBulkStatus(v as ActivityStatus | '')}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status ändern" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!bulkStatus || updating}
            onClick={handleBulkStatusChange}
          >
            {updating ? 'Wird angewendet…' : 'Status übernehmen'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            Auswahl aufheben
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      aria-label="Alle Aktivitäten auf dieser Seite auswählen"
                      checked={allPageSelected}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <Th label="Titel" sortKeyName="title" />
                  <Th label="Typ" sortKeyName="type" />
                  <Th label="Status" sortKeyName="status" />
                  <Th label="Priorität" sortKeyName="priority" />
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted">
                    Zugewiesen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted">
                    Team
                  </th>
                  <Th label="Start" sortKeyName="start_date" />
                  <Th label="Ende" sortKeyName="end_date" />
                  <Th label="Budget" sortKeyName="budget_planned" />
                  <th className="w-12 px-2 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((activity) => (
                  <tr
                    key={activity.id}
                    className="cursor-pointer border-b border-border transition-colors hover:bg-background dark:border-border/50 dark:hover:bg-card/30"
                    onClick={() => onActivityClick(activity.id)}
                  >
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        aria-label={`${activity.title} auswählen`}
                        checked={selectedIds.has(activity.id)}
                        onCheckedChange={() => toggleSelect(activity.id)}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">{activity.title}</td>
                    <td className="px-4 py-3 text-sm">{activity.type}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={activity.status}
                        onValueChange={(v) =>
                          handleStatusChange(activity.id, v as ActivityStatus)
                        }
                      >
                        <SelectTrigger className="h-8 w-[130px] border-0 bg-transparent shadow-none focus:ring-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        className={cn(
                          'text-xs',
                          priorityColors[activity.priority]
                        )}
                      >
                        {activity.priority}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {activity.assignee ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-[10px]">
                              {getInitials(activity.assignee.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">
                            {activity.assignee.full_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {activity.team?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">
                      {activity.start_date
                        ? formatDate(activity.start_date)
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">
                      {activity.end_date
                        ? formatDate(activity.end_date)
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatCurrency(activity.budget_planned)}
                    </td>
                    <td
                      className="px-2 py-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted hover:text-foreground"
                            aria-label="Aktivitäten-Aktionen"
                            disabled={deactivatingId === activity.id}
                          >
                            {deactivatingId === activity.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => onActivityClick(activity.id)}>
                            Öffnen
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-danger focus:text-danger"
                            onClick={() => void handleDeactivateActivity(activity)}
                          >
                            Deaktivieren…
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-muted">Keine Aktivitäten zur Suche gefunden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">
            Seite {currentPage + 1} von {totalPages} ({sorted.length} Einträge)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              aria-label="Vorherige Seite"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Nächste Seite"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
